import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../api/useApi";
import { ensureAccount, fetchCredits, redeemCode } from "../api/resources";

/**
 * /redeem?code=GIFT-XXXXXX — where a gift card is used (Billing/GiftCards.cs). Public: a signed-out recipient sees
 * the code and signs up, coming back here with it; signed in, the account is made sure of (as AccountEnsurer does) and
 * the code redeemed once, then it is on to the first upload.
 */
export default function Redeem() {
  const { isLoading, isAuthenticated, user, loginWithRedirect } = useAuth0();
  const apiFetch = useApi();
  // The api function may be new on each render; the redeem runs once, not on each.
  const apiRef = useRef(apiFetch);
  apiRef.current = apiFetch;
  const location = useLocation();
  const fromLink = (new URLSearchParams(location.search).get("code") ?? "").trim().toUpperCase();
  const [code, setCode] = useState(fromLink);
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");
  const [available, setAvailable] = useState(0);
  const tried = useRef(false);

  const redeem = useCallback(
    async (value: string) => {
      const typed = value.trim().toUpperCase();
      if (!typed || !user) return;
      setState("busy");
      try {
        await ensureAccount(apiRef.current, user);
        await redeemCode(apiRef.current, typed);
        const credits = await fetchCredits(apiRef.current).catch(() => null);
        setAvailable(Number(credits?.available) || 0);
        setState("done");
      } catch {
        setState("error");
      }
    },
    [user]
  );

  useEffect(() => {
    if (isLoading || !isAuthenticated || !fromLink || tried.current) return;
    tried.current = true;
    void redeem(fromLink);
  }, [isLoading, isAuthenticated, fromLink, redeem]);

  const returnTo = "/redeem" + (code ? "?code=" + encodeURIComponent(code.trim().toUpperCase()) : "");

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-md px-4 py-12">
        <h1 className="text-2xl font-bold text-slate-900">Redeem your Eleetz gift</h1>
        {state === "done" ? (
          <div className="mt-6 card p-5">
            <p className="text-slate-800">{`Done: ${available} premium ${available === 1 ? "analysis is" : "analyses are"} yours.`}</p>
            <p className="mt-1 text-sm text-slate-500">Each one lasts 30 days. Film your swing and upload it to use one.</p>
            <Link to="/upload" className="btn-primary mt-4 inline-flex">Upload your swing</Link>
          </div>
        ) : (
          <div className="mt-6 card p-5 space-y-3">
            {fromLink && !isAuthenticated && <div className="font-mono text-xl tracking-wider text-slate-900">{fromLink}</div>}
            {(isAuthenticated || !fromLink) && (
              <label className="block text-sm text-slate-600">
                Gift code
                <input
                  aria-label="Gift code"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono uppercase"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="GIFT-XXXXXX"
                />
              </label>
            )}
            {state === "error" && <p className="text-sm text-rose-600">That code is not valid or has already been used.</p>}
            {isAuthenticated ? (
              <button type="button" className="btn-primary" disabled={state === "busy" || !code.trim()} onClick={() => void redeem(code)}>
                {state === "busy" ? "Redeeming…" : "Redeem"}
              </button>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => void loginWithRedirect({ authorizationParams: { screen_hint: "signup" }, appState: { returnTo } })}
                >
                  Sign up to redeem
                </button>
                <button type="button" className="btn-secondary" onClick={() => void loginWithRedirect({ appState: { returnTo } })}>
                  Log in
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
